/**
 * EngravingFeature - Optimized engraving management system
 *
 * Performance Optimizations:
 * - Cart data caching (3s timeout) to reduce API calls
 * - Debounced cart rule enforcement (800ms) to prevent excessive processing
 * - Smart cart count updates (avoids redundant DOM updates)
 * - Section rendering API for efficient HTML updates
 * - Rate limiting on cart rules (1.5s minimum between calls)
 * - Periodic cleanup reduced to 10s intervals to prevent server overload
 * - Single responsibility: each method handles one aspect of cart updates
 */
class EngravingFeature {
  constructor(config = {}) {
    // Configuration with defaults
    this.config = {
      engravingFee: config.engravingFee || 15,
      engravingFeeVariantId: config.engravingFeeVariantId || 56490232414592,
      maxEngravingLength: config.maxEngravingLength || 50,
      currency: config.currency || window.Shopify?.currency?.active || 'EUR',
      ...config,
    };

    // State management
    this.state = {
      engravingData: null,
      basePrice: 0,
      isPopupOpen: false,
      isProcessing: false,
      justAddedItems: false,
      currencySymbol: this.getCurrencySymbol(),
    };

    // Performance optimizations
    this.isEnforcingRules = false;
    this.cartCache = null;
    this.cacheTimestamp = 0;
    this.cacheTimeout = 3000; // 3 seconds cache
    this.lastCartRulesCall = 0;
    this.cartRulesRateLimit = 1500; // Increased to 1.5 seconds for better performance

    // DOM element cache with lazy loading
    this.elements = {};
    this.elementsInitialized = false;

    // Debounced functions with optimized timing
    this.debouncedCartRules = this.debounce(this.enforceCartRules.bind(this), 800);
    this.debouncedCleanup = this.debounce(this.forceOrphanedFeeCleanup.bind(this), 400);

    // Event handlers bound to this instance
    this.boundHandlers = {
      documentClick: this.handleDocumentClick.bind(this),
      formSubmit: this.handleFormSubmit.bind(this),
      cartFormSubmit: this.handleCartFormSubmit.bind(this),
      cartUpdate: this.handleCartUpdate.bind(this),
      keydown: this.handleKeydown.bind(this),
    };

    // Observers for cleanup
    this.observers = new Set();

    this.init();
  }

  async init() {
    try {
      await this.cacheElements();
      this.setupEventListeners();
      this.setupAjaxMonitoring(); // Setup AJAX monitoring early for better cart change detection
      this.state.basePrice = this.getBasePrice();
      // Initial cart rules enforcement and orphaned cleanup - run on any page
      await this.enforceCartRules();
    } catch (error) {
      console.error('Failed to initialize engraving feature:', error);
      this.showErrorMessage('Failed to initialize engraving feature');
    }
  }

  /**
   * Cache DOM elements for performance with lazy loading
   */
  async cacheElements() {
    // Only cache elements if not already initialized
    if (this.elementsInitialized) return;

    // Use more specific selectors and cache frequently accessed elements
    const selectors = {
      triggerBtn: '.engraving-trigger-btn',
      popup: '#engraving-popup',
      form: '#engraving-form',
      textInput: '#engraving-text',
      priceDisplay: '.product__price .price-item, .price-item, .price .money',
      cartForm: 'form[action="/cart"]',
      productForms:
        'form[action="/cart/add"], form[action*="/cart/add"], [data-type="add-to-cart-form"], [id*="product-form"], [id*="add-to-cart"], .product-form, #product-form',
    };

    // Cache elements with error handling
    Object.keys(selectors).forEach((key) => {
      try {
        if (key === 'productForms') {
          this.elements[key] = document.querySelectorAll(selectors[key]);
        } else {
          this.elements[key] = document.querySelector(selectors[key]);
        }
      } catch (error) {
        console.warn(`Failed to cache element ${key}:`, error);
        this.elements[key] = key === 'productForms' ? [] : null;
      }
    });

    this.elementsInitialized = true;
  }

  /**
   * Setup all event listeners with optimized performance
   */
  setupEventListeners() {
    // Document-wide click handling with delegation
    document.addEventListener('click', this.boundHandlers.documentClick);

    // Form submission handling
    if (this.elements.form) {
      this.elements.form.addEventListener('submit', this.boundHandlers.formSubmit);
    }

    // Cart form handling with optimized event delegation
    if (this.elements.cartForm) {
      this.elements.cartForm.addEventListener('submit', this.boundHandlers.cartFormSubmit);

      // Use single event listener with delegation for cart form changes
      this.elements.cartForm.addEventListener(
        'input',
        (e) => {
          if (e.target.name === 'updates[]' || e.target.name === 'quantity') {
            this.debouncedCartRules();
          }
        },
        { passive: true }
      );
    }

    // Product form handling - prevent double submission
    this.elements.productForms?.forEach((form) => {
      // Check if we've already added a listener to this form
      if (form.hasAttribute('data-engraving-listener')) {
        return;
      }

      form.addEventListener(
        'submit',
        (e) => {
          const engravingData = this.collectEngravingDataFromDOM();
          if (engravingData) {
            // Prevent the default form submission and our custom handler
            e.preventDefault();
            e.stopImmediatePropagation();
            this.handleCartFormSubmit(e);
          }
        },
        { capture: true }
      ); // Use capture phase to intercept before other handlers

      // Mark this form as having our listener
      form.setAttribute('data-engraving-listener', 'true');
    });

    // Global form submission handler with better performance and double submission prevention
    // Only use this as a fallback for forms that weren't caught by the specific handlers above
    document.addEventListener(
      'submit',
      (e) => {
        // Skip if this form already has our specific listener
        if (e.target.hasAttribute('data-engraving-listener')) {
          return;
        }

        if (this.isProductForm(e.target)) {
          const engravingData = this.collectEngravingDataFromDOM();
          if (engravingData) {
            // Prevent the default form submission and our custom handler
            e.preventDefault();
            e.stopImmediatePropagation();
            this.handleCartFormSubmit(e);
          }
        }
      },
      { passive: false, capture: true } // Use capture phase to intercept before other handlers
    );

    // Keyboard navigation
    document.addEventListener('keydown', this.boundHandlers.keydown, { passive: true });

    // Optimized cart monitoring
    this.setupOptimizedCartMonitoring();

    // Cleanup on page unload
    window.addEventListener('beforeunload', () => this.cleanup(), { once: true });
  }

  /**
   * Handle document clicks with delegation
   */
  handleDocumentClick(event) {
    const target = event.target;

    // Open engraving popup
    if (target.classList.contains('engraving-trigger-btn')) {
      event.preventDefault();
      this.openPopup();
      return;
    }

    // Close popup
    if (target.classList.contains('engraving-popup-close') || target.closest('.engraving-popup-close')) {
      this.closePopup();
      return;
    }

    // Close on overlay click
    if (target.classList.contains('engraving-popup-overlay')) {
      event.preventDefault();
      this.closePopup();
      return;
    }

    // Cart quantity/remove button handling
    if (
      target.matches(
        '[name="update"], ' +
          '[name="updates[]"], ' +
          '.cart__remove, ' +
          '[data-cart-remove], ' +
          '.cart-remove, ' +
          '[href*="/cart/change"], ' +
          '.btn--remove, ' +
          '.cart__quantity-remove, ' +
          '.line-item__remove, ' +
          'input[type="number"]'
      )
    ) {
      // Get the cart item key if available for targeted cleanup
      const itemRow = target.closest('[data-cart-item-key], .cart__item, .line-item');
      const itemKey = itemRow?.getAttribute('data-cart-item-key');

      // Run enforcement with single debounced call to prevent conflicts
      this.debouncedCartRules();
    }
  }

  /**
   * Handle engraving form submission
   */
  async handleFormSubmit(event) {
    event.preventDefault();

    if (this.state.isProcessing) return;

    try {
      this.state.isProcessing = true;
      this.showLoadingState(true);

      if (await this.validateForm()) {
        this.state.engravingData = this.collectEngravingDataFromDOM();
        this.updateEngravingButton();
        this.updatePriceDisplay();
        this.closePopup();
        this.showSuccessMessage('Engraving configured successfully! Now click "Add to Cart"');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      this.showErrorMessage('Failed to save engraving configuration');
    } finally {
      this.state.isProcessing = false;
      this.showLoadingState(false);
    }
  }

  /**
   * Handle cart form submissions (product add to cart)
   */
  async handleCartFormSubmit(event) {
    const form = event.target;

    if (!this.isProductForm(form)) return;

    // Prevent double submission
    if (this.state.isProcessing) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    // Collect engraving data directly from the DOM before proceeding
    const engravingData = this.collectEngravingDataFromDOM();

    // Only intercept if there's engraving data, otherwise let Shopify handle it naturally
    if (engravingData) {
      // Ensure we prevent the default submission
      event.preventDefault();
      event.stopImmediatePropagation();

      try {
        this.state.isProcessing = true;
        this.showLoadingState(true);

        // Use the newly collected data for submission
        this.state.engravingData = engravingData;
        await this.addToCartWithEngraving(form);

        // ✅ FIX: Reset the state and clear form data after a successful add-to-cart
        this.state.engravingData = null;
        this.clearEngravingFormData();
      } catch (error) {
        console.error('Cart submission error:', error);
        this.showErrorMessage('Failed to add product to cart: ' + error.message);
      } finally {
        this.state.isProcessing = false;
        this.showLoadingState(false);
      }
    }
  }

  /**
   * Handle cart update events
   */
  async handleCartUpdate() {
    if (this.isOnCartPage()) {
      this.debouncedCartRules();
    }
  }

  /**
   * Handle keyboard events
   */
  handleKeydown(event) {
    if (event.key === 'Escape' && this.state.isPopupOpen) {
      this.closePopup();
    }
  }

  /**
   * Open the engraving popup with enhanced UX
   */
  openPopup() {
    if (!this.elements.popup || this.state.isPopupOpen) return;

    this.state.isPopupOpen = true;
    this.elements.popup.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    // Add animation class
    this.elements.popup.classList.add('engraving-popup-open');

    // Focus management
    requestAnimationFrame(() => {
      if (this.elements.textInput) {
        this.elements.textInput.focus();
      }
    });

    // Populate existing data if available
    this.populateFormData();
  }

  /**
   * Close the engraving popup
   */
  closePopup() {
    if (!this.elements.popup || !this.state.isPopupOpen) return;

    this.state.isPopupOpen = false;
    this.elements.popup.classList.remove('engraving-popup-open');

    setTimeout(() => {
      this.elements.popup.style.display = 'none';
      this.restoreBodyOverflow();
    }, 200);

    this.clearFormErrors();
  }

  /**
   * Enhanced form validation with visual feedback
   */
  async validateForm() {
    const errors = [];
    let isValid = true;

    // Clear previous errors
    this.clearFormErrors();

    // Validate engraving text
    const textInput = this.elements.textInput;
    const text = textInput?.value?.trim() || '';

    if (!text) {
      this.showFieldError(textInput, 'Please enter engraving text');
      isValid = false;
    } else if (text.length > this.config.maxEngravingLength) {
      this.showFieldError(textInput, `Text must be ${this.config.maxEngravingLength} characters or less`);
      isValid = false;
    }

    // Validate font selection
    const fontInput = document.querySelector('input[name="font-family"]:checked');
    if (!fontInput) {
      this.showFormError('Please select a font');
      isValid = false;
    }

    // Validate location selection
    const locationInput = document.querySelector('input[name="location"]:checked');
    if (!locationInput) {
      this.showFormError('Please select an engraving location');
      isValid = false;
    }

    return isValid;
  }

  /**
   * Collect form data from the DOM into a structured object.
   * Returns null if any required data is missing.
   */
  collectEngravingDataFromDOM() {
    const textValue = this.elements.textInput?.value?.trim() || '';
    const fontValue = document.querySelector('input[name="font-family"]:checked')?.value || '';
    const locationValue = document.querySelector('input[name="location"]:checked')?.value || '';

    if (!textValue || !fontValue || !locationValue) {
      return null;
    }

    return {
      'Engraving Text': textValue,
      Font: fontValue,
      Location: locationValue,
    };
  }

  /**
   * Populate form with existing data
   */
  populateFormData() {
    if (!this.state.engravingData) return;

    const engravingText = this.state.engravingData['Engraving Text'];
    const font = this.state.engravingData['Font'];
    const location = this.state.engravingData['Location'];

    if (this.elements.textInput && engravingText) {
      this.elements.textInput.value = engravingText;
    }

    if (font) {
      const fontRadio = document.querySelector(`input[name="font-family"][value="${font}"]`);
      if (fontRadio) fontRadio.checked = true;
    }

    if (location) {
      const locationRadio = document.querySelector(`input[name="location"][value="${location}"]`);
      if (locationRadio) locationRadio.checked = true;
    }
  }

  /**
   * Clear engraving form data after successful submission
   */
  clearEngravingFormData() {
    // Clear text input
    if (this.elements.textInput) {
      this.elements.textInput.value = '';
    }

    // Clear radio button selections
    const fontRadios = document.querySelectorAll('input[name="font-family"]');
    fontRadios.forEach((radio) => (radio.checked = false));

    const locationRadios = document.querySelectorAll('input[name="location"]');
    locationRadios.forEach((radio) => (radio.checked = false));

    // Reset button state
    if (this.elements.triggerBtn) {
      this.elements.triggerBtn.textContent = 'Add Engraving';
      this.elements.triggerBtn.classList.remove('engraving-selected');
    }

    // Reset price display
    if (this.elements.priceDisplay && this.state.basePrice > 0) {
      this.elements.priceDisplay.innerHTML = this.formatPrice(this.state.basePrice);
    }
  }

  /**
   * Update the engraving trigger button state
   */
  updateEngravingButton() {
    const btn = this.elements.triggerBtn;
    if (!btn) return;

    btn.textContent = '✓ Engraving Added';
    btn.classList.add('engraving-selected');

    // Add animation
    btn.style.transform = 'scale(1.05)';
    setTimeout(() => {
      btn.style.transform = '';
    }, 200);
  }

  /**
   * Update price display with engraving fee
   */
  updatePriceDisplay() {
    if (!this.state.engravingData || !this.elements.priceDisplay) return;

    // Refresh base price in case it wasn't detected initially
    if (this.state.basePrice === 0) {
      this.state.basePrice = this.getBasePrice();
    }

    const total = this.state.basePrice + this.config.engravingFee;

    this.elements.priceDisplay.innerHTML = `
      <div class="price-with-engraving">
        <span class="total-price">
          ${this.formatPrice(total)}
        </span>
        <div class="price-breakdown">
          <small class="original-price">
            Was: ${this.formatPrice(this.state.basePrice)}
          </small>
          <small class="engraving-fee">
            + ${this.formatPrice(this.config.engravingFee)} engraving fee
          </small>
        </div>
      </div>
    `;
  }

  /**
   * Add product to cart with engraving
   */
  async addToCartWithEngraving(form) {
    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity'), 10) || 1;
    const uniqueId = `${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;

    // --- New: Get product name and SKU ---
    let productName = '';
    let productSKU = '';
    // Try to get from Shopify product object or DOM
    if (window.meta?.product?.title) {
      productName = window.meta.product.title;
      productSKU = window.meta.product.variants?.find((v) => v.id == variantId)?.sku || '';
    } else if (window.product) {
      productName = window.product.title;
      productSKU = window.product.variants?.find((v) => v.id == variantId)?.sku || '';
    } else {
      // Fallback: Try to get from DOM
      productName = document.querySelector('.product__title')?.textContent?.trim() || '';
      productSKU = document.querySelector('[data-sku]')?.getAttribute('data-sku') || '';
    }

    // Final validation - ensure all required fields are present
    const textValue = this.state.engravingData['Engraving Text']?.trim();
    const fontValue = this.state.engravingData['Font'];
    const locationValue = this.state.engravingData['Location'];

    if (!textValue || !fontValue || !locationValue) {
      console.error('Missing engraving data:', { textValue, fontValue, locationValue });
      throw new Error(
        `Missing required engraving data. Text: ${!!textValue}, Font: ${!!fontValue}, Location: ${!!locationValue}`
      );
    }

    // Build properties for main product with validated data
    const engravingProperties = {
      'Engraving Text': textValue,
      Font: fontValue,
      Location: locationValue,
      EngravingID: uniqueId, // <-- changed to EngravingID (no underscore)
    };

    // --- Add name and SKU to engraving fee properties ---
    const engravingFeeProperties = {
      EngravingID: uniqueId, // <-- changed to EngravingID (no underscore)
      _is_engraving_fee: 'true',
      _linked_product: variantId,
      'Linked Product Name': productName,
      'Linked Product SKU': productSKU,
    };

    const cartItems = [
      {
        id: variantId,
        quantity,
        properties: engravingProperties,
      },
      {
        id: this.config.engravingFeeVariantId,
        quantity,
        properties: engravingFeeProperties,
      },
    ];

    try {
      // Add both items in a single request
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: cartItems }),
      });

      const responseText = await response.text();
      if (!response.ok) {
        throw new Error(`Failed to add items: ${response.status} ${responseText}`);
      }

      this.showSuccessMessage('Product with engraving and fee added to cart!');

      // Set flag to prevent immediate cart rules enforcement
      this.state.justAddedItems = true;

      // Get the updated cart data and refresh cart drawer
      // Add a delay to ensure cart API has processed the items
      setTimeout(async () => {
        await this.refreshCartAfterAdd();
      }, 800);

      // Delay cart rules enforcement to allow the cart to settle
      setTimeout(() => {
        this.state.justAddedItems = false;
        this.debouncedCartRules();
      }, 2000);
    } catch (error) {
      console.error('Add to cart error:', error);
      this.showErrorMessage('Failed to add product with engraving to cart: ' + error.message);
      throw error;
    }
  }

  /**
   * Enforce cart rules for engraving items with optimized performance
   */
  async enforceCartRules() {
    // Prevent infinite loops and conflicts
    if (this.isEnforcingRules) return;

    // Check for conflicts with other cart systems
    if (window.nominationValidation?.isValidating || window.CartManager?.isProcessing) return;

    // Skip enforcement if we just added items
    if (this.state.justAddedItems) return;

    // Rate limiting with improved logic
    const now = Date.now();
    if (now - this.lastCartRulesCall < this.cartRulesRateLimit) return;

    this.isEnforcingRules = true;
    this.lastCartRulesCall = now;

    try {
      const cart = await this.getCart();

      // Use more efficient filtering with early returns
      const engravedItems = cart.items.filter(
        (item) =>
          item.properties?.EngravingID &&
          !item.properties?._is_engraving_fee &&
          item.id !== this.config.engravingFeeVariantId
      );

      const engravingFeeItems = cart.items.filter(
        (item) => item.id === this.config.engravingFeeVariantId || item.properties?._is_engraving_fee
      );

      // Only process if there are items to check
      if (engravedItems.length === 0 && engravingFeeItems.length === 0) return;

      // Rule 1: Remove orphaned engraving fees (priority)
      const orphanedRemoved = await this.removeOrphanedFees(engravedItems, engravingFeeItems);

      // Rule 2: Sync quantities (only if no orphaned fees were removed)
      if (!orphanedRemoved) {
        await this.syncEngravingQuantities(engravedItems, engravingFeeItems);
      }
    } catch (error) {
      console.error('Cart rules enforcement failed:', error);
      // Clear cache on error to force fresh data on next call
      this.clearCartCache();
    } finally {
      this.isEnforcingRules = false;
    }
  }

  /**
   * Remove orphaned engraving fee items with optimized performance
   * Returns true if any orphaned fees were removed
   */
  async removeOrphanedFees(engravedItems, engravingFeeItems) {
    if (engravingFeeItems.length === 0) {
      return false;
    }

    // Create a map of valid engraved items by EngravingID for O(1) lookup
    const validEngravedItems = new Map();
    engravedItems.forEach((item) => {
      const engravingId = item.properties?.EngravingID;
      if (engravingId) {
        validEngravedItems.set(engravingId, item);
      }
    });

    // Filter orphaned fees more efficiently
    const orphanedFees = engravingFeeItems.filter((feeItem) => {
      const feeEngravingId = feeItem.properties?.EngravingID;
      const linkedProductId = feeItem.properties?._linked_product;

      // Case 1: Missing EngravingID
      if (!feeEngravingId) {
        return true;
      }

      // Case 2: No matching base product
      const matchingBaseProduct = validEngravedItems.get(feeEngravingId);
      if (!matchingBaseProduct) {
        return true;
      }

      // Case 3: Product ID mismatch
      if (linkedProductId && matchingBaseProduct.id.toString() !== linkedProductId) {
        return true;
      }

      return false;
    });

    if (orphanedFees.length === 0) return false;

    // Fetch fresh cart data before attempting removal to avoid 400 errors
    this.clearCartCache();
    const freshCart = await this.getCart();

    // Validate that items still exist in cart before attempting removal
    const validOrphanedFees = orphanedFees.filter((feeItem) => {
      return freshCart.items.some((cartItem) => cartItem.key === feeItem.key);
    });

    if (validOrphanedFees.length === 0) {
      // Items already removed, just clear cache and return
      this.clearCartCache();
      return false;
    }

    // Remove orphaned fees in parallel for better performance
    const removalPromises = validOrphanedFees.map(async (feeItem) => {
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: feeItem.key,
            quantity: 0,
          }),
        });

        if (!response.ok) {
          // Don't log 400 errors as they're expected when items are already removed
          if (response.status !== 400) {
            console.error(`Failed to remove orphaned fee ${feeItem.key}: ${response.status}`);
          }
          return false;
        }

        return true;
      } catch (error) {
        console.error(`Error removing orphaned fee ${feeItem.key}:`, error);
        return false;
      }
    });

    const results = await Promise.allSettled(removalPromises);
    const successCount = results.filter((result) => result.status === 'fulfilled' && result.value).length;

    if (successCount > 0) {
      // Clear cache immediately
      this.clearCartCache();

      // If on cart page, reload to ensure consistency
      if (this.isOnCartPage()) {
        setTimeout(() => window.location.reload(), 500);
      } else {
        // For cart drawer, refresh (this handles all updates including counts and body overflow)
        await this.refreshCartDrawer();
      }

      return true;
    }

    return false;
  }

  /**
   * Force orphaned fee cleanup (immediate execution)
   */
  async forceOrphanedFeeCleanup() {
    try {
      const cart = await this.getCart();

      // Skip if cart is empty or if there was a server error
      if (!cart || !cart.items || cart.items.length === 0) {
        return;
      }

      const engravedItems = cart.items.filter(
        (item) =>
          item.properties &&
          item.properties['EngravingID'] &&
          !item.properties['_is_engraving_fee'] &&
          item.id !== this.config.engravingFeeVariantId
      );

      const engravingFeeItems = cart.items.filter(
        (item) =>
          item.id === this.config.engravingFeeVariantId || (item.properties && item.properties['_is_engraving_fee'])
      );

      // Only run orphaned fee removal if there are actually fee items to check
      if (engravingFeeItems.length > 0) {
        // removeOrphanedFees handles all cart updates internally
        await this.removeOrphanedFees(engravedItems, engravingFeeItems);
      }
    } catch (error) {
      // Silently fail on network errors during periodic cleanup
      // This prevents console spam when server is under load
      if (
        !error.message ||
        (!error.message.includes('502') && !error.message.includes('503') && !error.message.includes('Failed to fetch'))
      ) {
        console.error('Force orphaned fee cleanup failed:', error);
      }
    }
  }

  /**
   * Sync engraving fee quantities with base product quantities
   */
  async syncEngravingQuantities(engravedItems, engravingFeeItems) {
    // Skip sync if we just added items (prevent immediate sync after addition)
    if (this.state.isProcessing || this.state.justAddedItems) {
      return;
    }

    const updates = [];

    engravedItems.forEach((engravedItem) => {
      const matchingFee = engravingFeeItems.find(
        (feeItem) => feeItem.properties['EngravingID'] === engravedItem.properties['EngravingID']
      );

      if (matchingFee && matchingFee.quantity !== engravedItem.quantity) {
        updates.push({
          key: matchingFee.key,
          quantity: engravedItem.quantity,
        });
      }
    });

    let updatedAny = false;
    for (const update of updates) {
      try {
        await this.updateCartItem(update.key, update.quantity);
        updatedAny = true;
      } catch (error) {
        console.error('Failed to sync quantity:', error);
      }
    }

    if (updatedAny) {
      // Trigger cart updated event
      document.dispatchEvent(new CustomEvent('cart:updated'));
    }
  }

  /**
   * Get current cart data with caching
   */
  async getCart() {
    const now = Date.now();

    // Return cached data if still valid
    if (this.cartCache && now - this.cacheTimestamp < this.cacheTimeout) {
      return this.cartCache;
    }

    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        // For server errors (5xx), use stale cache if available to reduce server load
        if (response.status >= 500) {
          if (this.cartCache) {
            return this.cartCache;
          }
        }
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }

      this.cartCache = await response.json();
      this.cacheTimestamp = now;
      return this.cartCache;
    } catch (error) {
      // Return cached data if available, even if stale
      if (this.cartCache) {
        // Only log warning for non-server errors
        if (!error.message || !error.message.includes('502')) {
          console.warn('Using stale cart cache due to fetch error');
        }
        return this.cartCache;
      }
      throw error;
    }
  }

  /**
   * Clear cart cache
   */
  clearCartCache() {
    this.cartCache = null;
    this.cacheTimestamp = 0;
  }

  /**
   * Trigger cart drawer to open after adding items
   */
  triggerCartDrawer() {
    try {
      // Try to find and open cart drawer
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        return;
      }

      // Fallback: Try to trigger cart drawer via cart icon click
      const cartIcon = document.querySelector(
        '[data-cart-drawer-toggle], .cart-drawer-toggle, [href*="cart"], .cart-link, #cart-icon, .cart-icon'
      );
      if (cartIcon) {
        cartIcon.click();
        return;
      }

      // Fallback: Dispatch cart update event to trigger drawer
      document.dispatchEvent(new CustomEvent('cart-update'));
    } catch (error) {
      console.error('Failed to trigger cart drawer:', error);
    }
  }

  /**
   * Refresh cart drawer content after removing items
   */
  async refreshCartDrawer() {
    try {
      // Clear cache first
      this.clearCartCache();

      // Strategy 1: Use section rendering for immediate visual update
      // This updates both cart-drawer and cart-icon-bubble HTML
      await this.refreshCartDrawerSections();

      // Strategy 2: Get fresh cart data and publish update
      const cartData = await this.getCart();
      if (cartData) {
        this.publishCartUpdate(cartData);
        // Note: updateCartCounts is called by publishCartUpdate, so no need to call it again
      }

      // Strategy 3: Ensure body overflow is restored
      this.restoreBodyOverflow();
    } catch (error) {
      console.error('Failed to refresh cart drawer:', error);
      // Ensure body overflow is restored even on error
      this.restoreBodyOverflow();
    }
  }

  /**
   * Refresh cart after adding items (for cart drawer display)
   */
  async refreshCartAfterAdd() {
    try {
      // Clear cache to get fresh data
      this.clearCartCache();

      // Wait for cart API to process - reduced delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Get updated cart data
      const cartData = await this.getCart();
      if (!cartData) {
        console.warn('No cart data received');
        this.triggerCartDrawer();
        return;
      }

      // Strategy 1: Use Shopify's section rendering for cart drawer
      await this.refreshCartDrawerSections();

      // Strategy 2: Publish cart update BEFORE opening drawer
      this.publishCartUpdate(cartData);

      // Small delay to ensure updates are processed
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Strategy 3: Open cart drawer after data is ready
      this.triggerCartDrawer();

      // Strategy 4: Safety net - update counts after drawer opens
      // This ensures counts are correct even if theme resets them during drawer open
      setTimeout(() => {
        this.updateCartCounts(cartData);
      }, 300);
    } catch (error) {
      console.error('Failed to refresh cart after add:', error);
      this.triggerCartDrawer();
    }
  }

  /**
   * Refresh cart drawer using Shopify's section rendering API
   */
  async refreshCartDrawerSections() {
    try {
      // Use Shopify's section rendering to get fresh cart drawer HTML
      const response = await fetch(`${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`);
      if (!response.ok) return;

      const sections = await response.json();

      // Update cart drawer content if available
      if (sections['cart-drawer']) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = sections['cart-drawer'];
          const newContent = tempDiv.querySelector('cart-drawer');
          if (newContent) {
            // Replace inner content, preserving the web component wrapper
            Array.from(newContent.attributes).forEach((attr) => {
              cartDrawer.setAttribute(attr.name, attr.value);
            });
            cartDrawer.innerHTML = newContent.innerHTML;
          }
        }
      }

      // Update cart icon bubble if available
      if (sections['cart-icon-bubble']) {
        const cartIconBubble = document.querySelector('cart-icon-bubble');
        if (cartIconBubble) {
          cartIconBubble.innerHTML = sections['cart-icon-bubble'];
        }
      }
    } catch (error) {
      console.error('Failed to refresh cart drawer sections:', error);
    }
  }

  /**
   * Publish cart update using theme's event system
   */
  publishCartUpdate(cartData) {
    try {
      // Use the theme's proper cart update event system
      if (window.PUB_SUB_EVENTS && window.publish) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'engraving-feature',
          cartData: cartData,
        });
      } else {
        // Fallback to custom event
        document.dispatchEvent(
          new CustomEvent('cart-update', {
            detail: { source: 'engraving-feature', cartData: cartData },
          })
        );
      }

      // Update cart counts
      this.updateCartCounts(cartData);
    } catch (error) {
      console.error('Failed to publish cart update:', error);
    }
  }

  /**
   * Force cart drawer refresh with comprehensive strategies
   */
  async forceCartDrawerRefresh(cartData) {
    try {
      // Strategy 1: Update cart counts immediately
      this.updateCartCounts(cartData);

      // Strategy 2: Dispatch multiple cart events
      this.dispatchCartEvents(cartData);

      // Strategy 3: Force cart drawer refresh if it exists
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        // Try different refresh methods
        if (typeof cartDrawer.refresh === 'function') {
          cartDrawer.refresh();
        }

        if (typeof cartDrawer.updateCart === 'function') {
          cartDrawer.updateCart(cartData);
        }

        // Try to force re-render by dispatching events on the cart drawer
        cartDrawer.dispatchEvent(new CustomEvent('cart:updated'));
        cartDrawer.dispatchEvent(new CustomEvent('cart:refresh'));
      }

      // Strategy 4: Update cart notification
      const cartNotification = document.querySelector('cart-notification');
      if (cartNotification && typeof cartNotification.renderContents === 'function') {
        cartNotification.renderContents(cartData);
      }

      // Strategy 5: Force page-level cart refresh
      await this.refreshCartSections();

      // Strategy 6: Additional delay and retry
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }, 200);

      // Strategy 7: Force cart drawer content update if still empty
      setTimeout(async () => {
        if (this.isCartDrawerOpen() && this.isCartDrawerEmpty()) {
          await this.forceCartDrawerContentUpdate(cartData);
        }
      }, 500);
    } catch (error) {
      console.error('Failed to force cart drawer refresh:', error);
    }
  }

  /**
   * Check if cart drawer is empty
   */
  isCartDrawerEmpty() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return true;

    // Check for empty cart indicators
    const emptyIndicator = cartDrawer.querySelector('.cart-drawer__empty, .cart-empty, [data-cart-empty]');
    if (emptyIndicator) return true;

    // Check for cart items
    const cartItems = cartDrawer.querySelectorAll('.cart-item, [data-cart-item], .cart-drawer__item');
    return cartItems.length === 0;
  }

  /**
   * Force cart drawer content update by directly manipulating DOM
   */
  async forceCartDrawerContentUpdate(cartData) {
    try {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || !cartData || !cartData.items) return;

      // Create basic cart content
      let cartContent = '';
      if (cartData.items.length === 0) {
        cartContent = '<div class="cart-drawer__empty">Your cart is empty</div>';
      } else {
        cartContent = '<div class="cart-drawer__items">';
        cartData.items.forEach((item) => {
          cartContent += `
            <div class="cart-item" data-cart-item-key="${item.key}">
              <div class="cart-item__image">
                <img src="${item.image}" alt="${item.title}" loading="lazy">
              </div>
              <div class="cart-item__details">
                <h3 class="cart-item__title">${item.product_title}</h3>
                <div class="cart-item__price">${this.formatPrice(item.price / 100)}</div>
                <div class="cart-item__quantity">Qty: ${item.quantity}</div>
              </div>
            </div>
          `;
        });
        cartContent += '</div>';

        // Add cart totals
        cartContent += `
          <div class="cart-drawer__footer">
            <div class="cart-drawer__total">
              <span>Subtotal: ${this.formatPrice(cartData.total_price / 100)}</span>
            </div>
          </div>
        `;
      }

      // Try to find and update cart content area
      const contentArea = cartDrawer.querySelector(
        '.cart-drawer__content, .cart-drawer__body, .cart-drawer__items, .cart-drawer__empty'
      );
      if (contentArea) {
        contentArea.innerHTML = cartContent;
      } else {
        // Fallback: try to update the main cart drawer content
        const mainContent =
          cartDrawer.querySelector('[data-cart-drawer-content]') || cartDrawer.querySelector('.cart-drawer__main');
        if (mainContent) {
          mainContent.innerHTML = cartContent;
        }
      }

      // Update cart count
      this.updateCartCounts(cartData);
    } catch (error) {
      console.error('Failed to force cart drawer content update:', error);
    }
  }

  /**
   * Format price for display
   */
  formatPrice(price) {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(price);
  }

  /**
   * Force immediate cart update using multiple strategies
   */
  async forceImmediateCartUpdate(cartData) {
    try {
      // Use native Shopify cart functionality - just dispatch the standard event
      // Let the theme handle all cart drawer updates naturally
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { cart: cartData },
        })
      );

      // Also try theme-specific events if available
      if (window.PUB_SUB_EVENTS?.cartUpdate) {
        document.dispatchEvent(
          new CustomEvent(window.PUB_SUB_EVENTS.cartUpdate, {
            detail: { cart: cartData },
          })
        );
      }
    } catch (error) {
      console.error('Failed to update cart:', error);
    }
  }

  /**
   * Refresh cart sections to ensure cart drawer shows updated content
   */
  async refreshCartSections() {
    try {
      // Strategy 1: Dispatch standard cart events
      document.dispatchEvent(new CustomEvent('cart:updated'));

      // Strategy 2: Try theme-specific cart update event
      if (window.PUB_SUB_EVENTS?.cartUpdate) {
        document.dispatchEvent(new CustomEvent(window.PUB_SUB_EVENTS.cartUpdate));
      }

      // Strategy 3: Try Cart Manager if available
      if (window.CartManager) {
        window.CartManager.notifyListeners('cart:updated', {});
      }

      // Strategy 4: Try to trigger cart drawer refresh via theme events
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        // Dispatch cart drawer specific events
        cartDrawer.dispatchEvent(new CustomEvent('cart:updated'));
        cartDrawer.dispatchEvent(new CustomEvent('cart:refresh'));
      }

      // Strategy 5: Force a small delay and retry if cart drawer is open
      if (this.isCartDrawerOpen()) {
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('cart:updated'));
        }, 100);
      }
    } catch (error) {
      console.error('Failed to refresh cart sections:', error);
    }
  }

  /**
   * Update cart counts in header and drawer
   * Note: This is a fallback method. When using refreshCartDrawerSections(),
   * the counts are automatically updated via HTML replacement.
   */
  updateCartCounts(cartData) {
    if (!cartData || cartData.item_count === undefined) return;

    // Force update cart count in header
    const cartCount = document.querySelector('.cart__items-count, [data-cart-count]');
    if (cartCount) {
      cartCount.textContent = cartData.item_count;
    }

    // Update cart icon bubble (the number badge on cart icon)
    const cartIconBubble = document.querySelector('cart-icon-bubble');
    if (cartIconBubble) {
      // Update the visible count (first span with aria-hidden)
      const bubbleContainer = cartIconBubble.querySelector('.cart-count-bubble');
      if (bubbleContainer) {
        const visibleCount = bubbleContainer.querySelector('span[aria-hidden="true"]');
        const hiddenCount = bubbleContainer.querySelector('span.visually-hidden');

        if (visibleCount) {
          visibleCount.textContent = cartData.item_count;
        }
        if (hiddenCount) {
          hiddenCount.textContent = `${cartData.item_count} items`;
        }

        // Show/hide bubble container based on count
        if (cartData.item_count > 0) {
          bubbleContainer.classList.remove('hidden');
        } else {
          bubbleContainer.classList.add('hidden');
        }
      }
    }

    // Also try updating by ID (fallback for different theme structures)
    const cartBubbleById = document.getElementById('cart-icon-bubble');
    if (cartBubbleById) {
      const bubbleContainer = cartBubbleById.querySelector('.cart-count-bubble');
      if (bubbleContainer) {
        const visibleCount = bubbleContainer.querySelector('span[aria-hidden="true"]');
        const hiddenCount = bubbleContainer.querySelector('span.visually-hidden');

        if (visibleCount) {
          visibleCount.textContent = cartData.item_count;
        }
        if (hiddenCount) {
          hiddenCount.textContent = `${cartData.item_count} items`;
        }
      }
    }

    // Force update cart drawer title
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const cartTitle = cartDrawer.querySelector('.drawer__heading');
      if (cartTitle) {
        const countSpan = cartTitle.querySelector('.cart__items-count');
        if (countSpan) {
          countSpan.textContent = cartData.item_count;
        }
      }
    }
  }

  /**
   * Update cart drawer with new data
   */
  async updateCartDrawer(cartData) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    // Update cart counts
    this.updateCartCounts(cartData);

    // Dispatch cart update events to let theme handle refresh
    this.dispatchCartEvents(cartData);

    // Try to update cart notification
    const cartNotification = document.querySelector('cart-notification');
    if (cartNotification && typeof cartNotification.renderContents === 'function') {
      cartNotification.renderContents(cartData);
    }
  }

  /**
   * Dispatch cart update events
   */
  dispatchCartEvents(cartData) {
    // Standard cart update event
    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: { cartData },
      })
    );

    // Theme-specific events
    if (window.PUB_SUB_EVENTS?.cartUpdate) {
      document.dispatchEvent(
        new CustomEvent(window.PUB_SUB_EVENTS.cartUpdate, {
          detail: { source: 'engraving-feature', cartData },
        })
      );
    }

    // Cart Manager events
    if (window.CartManager) {
      window.CartManager.notifyListeners('cart:updated', { cartData });
    }
  }

  /**
   * Update cart item quantity
   */
  async updateCartItem(key, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update cart item: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Remove cart item
   */
  async removeCartItem(key) {
    return this.updateCartItem(key, 0);
  }

  /**
   * Reload cart page
   */
  reloadCart() {
    if (this.isOnCartPage()) {
      setTimeout(() => location.reload(), 300);
    }
  }

  // Utility methods

  /**
   * Restore body overflow to fix frozen screen issue
   */
  restoreBodyOverflow() {
    try {
      // Remove overflow hidden from body
      document.body.style.overflow = '';
      document.body.style.removeProperty('overflow');

      // Also check and restore on html element
      document.documentElement.style.overflow = '';
      document.documentElement.style.removeProperty('overflow');
    } catch (error) {
      console.error('Failed to restore body overflow:', error);
    }
  }

  isEngravingEnabled() {
    return !!this.elements.triggerBtn;
  }

  isOnCartPage() {
    return window.location.pathname.includes('/cart');
  }

  isCartDrawerOpen() {
    // Check if cart drawer is open by looking for common cart drawer indicators
    const cartDrawer = document.querySelector('#CartDrawer, .cart-drawer, [data-cart-drawer]');
    if (!cartDrawer) return false;

    // Check if drawer is visible
    const isVisible =
      cartDrawer.offsetParent !== null ||
      cartDrawer.style.display !== 'none' ||
      !cartDrawer.classList.contains('hidden') ||
      cartDrawer.classList.contains('open') ||
      cartDrawer.classList.contains('active');

    return isVisible;
  }

  isProductForm(form) {
    if (!form) return false;

    const hasVariantInput = !!form.querySelector('input[name="id"]');
    const hasCartAction =
      form.getAttribute('action') === '/cart/add' || (form.action && form.action.includes('/cart/add'));
    const hasProductData =
      form.getAttribute('data-type') === 'add-to-cart-form' ||
      form.id.includes('product-form') ||
      form.id.includes('add-to-cart') ||
      form.classList.contains('product-form');

    return hasVariantInput || hasCartAction || hasProductData;
  }

  getBasePrice() {
    // Try multiple sources for product price

    // Method 1: Shopify product object
    if (window.meta?.product?.price) return window.meta.product.price / 100;
    if (window.product?.price) return window.product.price / 100;

    // Method 2: Try data attributes
    const priceElement = document.querySelector('[data-product-price], [data-price], .price .money');
    if (priceElement) {
      const priceText =
        priceElement.textContent ||
        priceElement.getAttribute('data-product-price') ||
        priceElement.getAttribute('data-price');
      if (priceText) {
        const price = parseFloat(priceText.replace(/[^\d.,]/g, '').replace(',', '.'));
        if (!isNaN(price) && price > 0) return price;
      }
    }

    // Method 3: Try to find price in various selectors
    const priceSelectors = [
      '.product__price .money',
      '.price-item .money',
      '.product-price .money',
      '.price .money',
      '[data-product-price]',
      '.price-item',
      '.product__price',
    ];

    for (const selector of priceSelectors) {
      const element = document.querySelector(selector);
      if (element) {
        const priceText = element.textContent;
        if (priceText) {
          const price = parseFloat(priceText.replace(/[^\d.,]/g, '').replace(',', '.'));
          if (!isNaN(price) && price > 0) return price;
        }
      }
    }

    console.warn('Could not detect base product price');
    return 299; // Fallback price based on screenshot
  }

  formatPrice(price) {
    const symbol = this.state.currencySymbol;
    const formatted = price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    switch (this.config.currency) {
      case 'EUR':
        return `${formatted}€`;
      case 'USD':
      case 'CAD':
      case 'AUD':
        return `${symbol}${formatted}`;
      default:
        return `${symbol}${formatted}`;
    }
  }

  getCurrencySymbol() {
    const symbols = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      CAD: 'C$',
      AUD: 'A$',
      JPY: '¥',
      CHF: 'Fr.',
    };
    return symbols[this.config.currency] || this.config.currency;
  }

  // UI feedback methods

  showSuccessMessage(message, duration = 3000) {
    this.showMessage(message, 'success', duration);
  }

  showErrorMessage(message, duration = 5000) {
    this.showMessage(message, 'error', duration);
  }

  showMessage(message, type = 'info', duration = 3000) {
    const existing = document.querySelector('.engraving-message');
    if (existing) existing.remove();

    const div = document.createElement('div');
    div.className = `engraving-message engraving-message-${type}`;
    div.textContent = message;

    document.body.appendChild(div);

    // Animate in
    requestAnimationFrame(() => {
      div.classList.add('engraving-message-show');
    });

    setTimeout(() => {
      div.classList.remove('engraving-message-show');
      setTimeout(() => div.remove(), 300);
    }, duration);
  }

  showFieldError(field, message) {
    if (!field) return;

    field.classList.add('form-input-error');

    let errorDiv = field.parentNode.querySelector('.form-error');
    if (!errorDiv) {
      errorDiv = document.createElement('div');
      errorDiv.className = 'form-error';
      field.parentNode.appendChild(errorDiv);
    }

    errorDiv.textContent = message;
    errorDiv.classList.add('show');
  }

  showFormError(message) {
    let errorDiv = document.querySelector('#engraving-form .form-general-error');
    if (!errorDiv) {
      errorDiv = document.createElement('div');
      errorDiv.className = 'form-error form-general-error';
      this.elements.form.appendChild(errorDiv);
    }

    errorDiv.textContent = message;
    errorDiv.classList.add('show');
  }

  clearFormErrors() {
    const errors = document.querySelectorAll('#engraving-form .form-error');
    const errorInputs = document.querySelectorAll('#engraving-form .form-input-error');

    errors.forEach((error) => error.classList.remove('show'));
    errorInputs.forEach((input) => input.classList.remove('form-input-error'));
  }

  showLoadingState(show) {
    const submitBtn = document.querySelector('#engraving-form [type="submit"]');
    if (!submitBtn) return;

    if (show) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Saving...';
    } else {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Apply Engraving';
    }
  }

  // Utility functions

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Setup optimized cart monitoring with better performance
   */
  setupOptimizedCartMonitoring() {
    // Setup Cart Manager integration if available
    this.setupCartManagerIntegration();

    // Setup DOM observers for cart changes
    this.setupCartObservers();

    // Setup periodic cleanup as backup
    this.setupPeriodicCleanup();
  }

  /**
   * Setup AJAX monitoring for cart API calls
   * This monitors all fetch/XHR calls to cart endpoints
   */
  setupAjaxMonitoring() {
    // Prevent duplicate setup
    if (this._ajaxMonitoringActive) return;
    this._ajaxMonitoringActive = true;

    // Intercept fetch API for cart endpoints
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      const url = args[0];

      // Check if this is a cart-related request
      if (typeof url === 'string' && (url.includes('/cart/') || url.includes('cart.js'))) {
        // Clone response to avoid consuming the stream
        const clonedResponse = response.clone();

        // Handle cart changes after the response is processed
        if (url.includes('/cart/change.js') || url.includes('/cart/update.js') || url.includes('/cart/add.js')) {
          // Wait for the response to be fully processed
          clonedResponse
            .json()
            .then(() => {
              // Clear cache to force fresh data
              this.clearCartCache();

              // For removals, force immediate orphaned fee cleanup
              if (url.includes('/cart/change.js') && url.includes('quantity:0')) {
                setTimeout(() => this.forceOrphanedFeeCleanup(), 300);
              }

              // Debounced cart rules for other changes
              setTimeout(() => this.debouncedCartRules(), 500);
            })
            .catch(() => {
              // Silent fail for non-JSON responses
            });
        }
      }

      return response;
    };

    // Also monitor XMLHttpRequest for older implementations
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this._url = url;
      return originalOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (...args) {
      if (this._url && (this._url.includes('/cart/') || this._url.includes('cart.js'))) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) {
            // Clear cache and trigger cleanup
            window.engravingFeature.clearCartCache();

            if (this._url.includes('/cart/change.js')) {
              setTimeout(() => window.engravingFeature.forceOrphanedFeeCleanup(), 300);
            }

            setTimeout(() => window.engravingFeature.debouncedCartRules(), 500);
          }
        });
      }
      return originalSend.call(this, ...args);
    };
  }

  /**
   * Setup Cart Manager integration for better performance
   */
  setupCartManagerIntegration() {
    if (!window.CartManager) return;

    // Listen for cart updates
    const unsubscribeCartUpdated = window.CartManager.on('cart:updated', () => this.debouncedCartRules(), this);

    // Listen for item removal
    const unsubscribeItemRemove = window.CartManager.on(
      'cart:item-remove',
      () => {
        setTimeout(() => this.forceOrphanedFeeCleanup(), 300);
        this.debouncedCartRules();
      },
      this
    );

    // Listen for item addition
    const unsubscribeItemAdded = window.CartManager.on('cart:item-added', () => this.debouncedCartRules(), this);

    // Store unsubscribe functions
    this.cartManagerUnsubscribers = [unsubscribeCartUpdated, unsubscribeItemRemove, unsubscribeItemAdded];
  }

  /**
   * Setup DOM observers for cart changes
   */
  setupCartObservers() {
    const cartSelectors = [
      '.cart',
      '#cart',
      '[data-cart]',
      '.cart-items',
      '.cart__contents',
      '.cart-drawer__contents',
      '#CartDrawer-CartItems',
      '#main-cart-items',
      '.cart-drawer',
      '.cart__items',
      'cart-drawer-items',
    ];

    const cartContainers = document.querySelectorAll(cartSelectors.join(', '));
    if (cartContainers.length === 0) return;

    cartContainers.forEach((container) => {
      const observer = new MutationObserver((mutations) => {
        let shouldCheck = false;
        let hasRemovedItems = false;

        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            if (mutation.removedNodes.length > 0) {
              shouldCheck = true;
              hasRemovedItems = true;
              break;
            }
            if (mutation.addedNodes.length > 0) {
              shouldCheck = true;
              break;
            }
          }
          if (
            mutation.type === 'attributes' &&
            ['data-cart-item-key', 'style', 'class'].includes(mutation.attributeName)
          ) {
            shouldCheck = true;
            break;
          }
        }

        if (shouldCheck) {
          if (hasRemovedItems) {
            // Immediate cleanup for item removals with shorter delay
            setTimeout(() => this.forceOrphanedFeeCleanup(), 100);
          } else {
            // Use debounced cleanup for other changes
            this.debouncedCleanup();
          }
        }
      });

      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-cart-item-key', 'style', 'class'],
      });

      this.observers.add(observer);
    });
  }

  /**
   * Setup periodic cleanup as backup mechanism
   */
  setupPeriodicCleanup() {
    // Cleanup every 10 seconds as backup (reduced frequency to prevent server overload)
    this.periodicCleanup = setInterval(async () => {
      if (!this.state.isProcessing && !this.isEnforcingRules) {
        try {
          await this.forceOrphanedFeeCleanup();
        } catch (error) {
          // Only log if it's not a network error (502, 503, etc.)
          if (error.message && !error.message.includes('502') && !error.message.includes('503')) {
            console.error('Periodic cleanup failed:', error);
          }
        }
      }
    }, 10000);
  }

  /**
   * Cleanup method for proper resource management
   */
  cleanup() {
    // Remove event listeners
    document.removeEventListener('click', this.boundHandlers.documentClick);
    document.removeEventListener('keydown', this.boundHandlers.keydown);

    // Disconnect all observers
    this.observers.forEach((observer) => observer.disconnect());
    this.observers.clear();

    // Clear intervals
    if (this.periodicCleanup) {
      clearInterval(this.periodicCleanup);
    }

    // Clean up Cart Manager subscriptions
    if (this.cartManagerUnsubscribers) {
      this.cartManagerUnsubscribers.forEach((unsubscribe) => unsubscribe());
    }

    // Remove form event listeners
    if (this.elements.form) {
      this.elements.form.removeEventListener('submit', this.boundHandlers.formSubmit);
    }

    if (this.elements.cartForm) {
      this.elements.cartForm.removeEventListener('submit', this.boundHandlers.cartFormSubmit);
    }

    this.elements.productForms?.forEach((form) => {
      form.removeEventListener('submit', this.boundHandlers.cartFormSubmit);
    });

    // Clear cache
    this.clearCartCache();
  }
}

// Global initialization
document.addEventListener('DOMContentLoaded', () => {
  // Initialize with default configuration
  // Can be customized by setting window.engravingConfig before DOM ready
  const config = window.engravingConfig || {};

  window.engravingFeature = new EngravingFeature(config);

  // Expose debugging methods globally
  window.engravingDebug = {
    forceOrphanedCleanup: () => {
      console.log('Manually triggering orphaned fee cleanup...');
      window.engravingFeature.forceOrphanedFeeCleanup();
    },
    enforceCartRules: () => {
      console.log('Manually triggering cart rules enforcement...');
      window.engravingFeature.enforceCartRules();
    },
    getCart: async () => {
      const cart = await window.engravingFeature.getCart();
      console.log('Current cart:', cart);
      return cart;
    },
    checkOrphanedFees: async () => {
      const cart = await window.engravingFeature.getCart();
      const engravedItems = cart.items.filter(
        (item) =>
          item.properties &&
          item.properties['EngravingID'] &&
          !item.properties['_is_engraving_fee'] &&
          item.id !== window.engravingFeature.config.engravingFeeVariantId
      );
      const engravingFeeItems = cart.items.filter(
        (item) =>
          item.id === window.engravingFeature.config.engravingFeeVariantId ||
          (item.properties && item.properties['_is_engraving_fee'])
      );

      console.log(
        'Engraved items:',
        engravedItems.map((item) => ({
          id: item.id,
          title: item.product_title,
          engravingId: item.properties?.EngravingID,
        }))
      );

      console.log(
        'Engraving fee items:',
        engravingFeeItems.map((item) => ({
          id: item.id,
          key: item.key,
          engravingId: item.properties?.EngravingID,
          linkedProduct: item.properties?.['Linked Product Name'],
          linkedId: item.properties?._linked_product,
        }))
      );

      return { engravedItems, engravingFeeItems };
    },
    checkCartDrawer: () => {
      console.log('Cart drawer open:', window.engravingFeature.isCartDrawerOpen());
      const cartDrawer = document.querySelector('#CartDrawer, .cart-drawer, [data-cart-drawer]');
      console.log('Cart drawer element:', cartDrawer);
      if (cartDrawer) {
        console.log('Cart drawer classes:', cartDrawer.className);
        console.log('Cart drawer style:', cartDrawer.style.display);
        console.log('Cart drawer offsetParent:', cartDrawer.offsetParent);
      }
    },
    testCartDrawerCleanup: () => {
      if (window.engravingFeature.isCartDrawerOpen()) {
        window.engravingFeature.forceOrphanedFeeCleanup();
      }
    },
    debugFormSubmission: () => {
      console.log('=== Form Submission Debug ===');
      const forms = document.querySelectorAll('form[action*="/cart/add"], form[data-type="add-to-cart-form"]');
      console.log('Found product forms:', forms.length);
      forms.forEach((form, index) => {
        console.log(`Form ${index + 1}:`, {
          id: form.id,
          action: form.action,
          hasEngravingListener: form.hasAttribute('data-engraving-listener'),
          hasVariantInput: !!form.querySelector('input[name="id"]'),
          isProductForm: window.engravingFeature.isProductForm(form),
        });
      });

      const engravingData = window.engravingFeature.collectEngravingDataFromDOM();
      console.log('Current engraving data:', engravingData);
      console.log('Is processing:', window.engravingFeature.state.isProcessing);
    },
    testCartDrawer: () => {
      const cartDrawer = document.querySelector('cart-drawer');
      console.log('Cart drawer element:', cartDrawer);
      console.log('Cart drawer open:', window.engravingFeature.isCartDrawerOpen());
      console.log(
        'Cart drawer methods:',
        cartDrawer ? Object.getOwnPropertyNames(Object.getPrototypeOf(cartDrawer)) : 'N/A'
      );
      window.engravingFeature.triggerCartDrawer();
    },
    testCartRefresh: () => {
      window.engravingFeature.refreshCartDrawer();
    },
    testCartAfterAdd: async () => {
      await window.engravingFeature.refreshCartAfterAdd();
    },
    debugCartData: async () => {
      const cartData = await window.engravingFeature.getCart();
      console.log('Cart data:', cartData);
      console.log('Cart drawer element:', document.querySelector('cart-drawer'));
      console.log('Cart notification element:', document.querySelector('cart-notification'));
    },
    testCartDrawerRefresh: async () => {
      console.log('=== Testing Cart Drawer Refresh ===');
      const cartData = await window.engravingFeature.getCart();
      console.log('Current cart data:', cartData);
      await window.engravingFeature.refreshCartDrawer();
      console.log('Cart drawer refresh completed');
    },
  };
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = EngravingFeature;
}
